import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  AppBar,
  Toolbar,
  Paper,
  InputAdornment,
  Grid,
  IconButton,
  Tooltip,
  InputLabel,
  Checkbox, FormControl,
  Menu,
  FormControlLabel,
  Card,
  CardContent
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DraggableTable from '../components/DraggableTable';
import simplefinTransactions from '../services/simplefin.service';
import StatusCell from '../components/StatusCell';
import NotesSidebar from '../components/NotesSidebar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

// Helper para sa currency format
const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(amount || 0);
};

export default function SimpleFINAccountsPage() {
  // --- STATE ---
  const [accounts, setAccounts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Filters
  const [keyword, setKeyword] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [bankSearch, setBankSearch] = useState('');
  const [lastSyncStatus, setLastSyncStatus] = useState('');
  
  // Range Filters
  const [minBalance, setMinBalance] = useState('');
  const [maxBalance, setMaxBalance] = useState('');
  const [minCredit, setMinCredit] = useState('');
  const [maxCredit, setMaxCredit] = useState('');

  // Sorting
  const [orderBy, setOrderBy] = useState('name');
  const [orderDirection, setOrderDirection] = useState('asc');

  // Status labels and editing
  const [statusLabels, setStatusLabels] = useState([]);
  const [editingLabels, setEditingLabels] = useState(false);
  const [labelsText, setLabelsText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Notes sidebar
  const [notesOpen, setNotesOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  
  // Bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');

  // --- 👈 NEW: COLUMN VISIBILITY STATE ---
  const allColumnIds = [
    'id', 'customer_name', 'name', 'bank', 'balance', 
    'total_credit_line', 'last_synced', 'last_sync_status', 
    'apr', 'user_name', 'card_status',
    'last_statement_opening', 'last_statement_closing', 'payment_autopay_date', 'autopay_bank_account'
  ];
  
  const defaultVisible = allColumnIds.filter(id => id !== 'bank' && id !== 'user_name' && id !== 'id');
  const [visibleColumns, setVisibleColumns] = useState(defaultVisible);
  const [colMenuAnchor, setColMenuAnchor] = useState(null);

  // Load labels and column prefs
  useEffect(() => {
    const loadData = async () => {
      try {
        const labels = await simplefinTransactions.getStatusLabels();
        setStatusLabels(labels);
      } catch (e) {
        console.error('Error loading status labels:', e);
        setStatusLabels(['Active', 'Exiting']);
      }

      try {
        const rawCols = localStorage.getItem('simplefin_visible_columns');
        if (rawCols) {
          setVisibleColumns(JSON.parse(rawCols));
        }
      } catch (e) {
        console.error('Error loading visible columns:', e);
      }
    };
    loadData();
  }, []);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const getShortSyncStatus = (status) => {
    if (!status) return '-';
    const text = status.toString().trim();
    if (text === 'Requires re-authentication') return 'Reauth';
    if (text === 'Not Synced') return 'Not Synced';
    if (text === 'Requires reauth') return 'Reauth';
    return text;
  };

  const clearSelection = () => { setSelectedIds(new Set()); setBulkStatus(''); };

  const handleSummaryClick = (status) => {
    // For SimpleFIN we just reuse account fetch with filter
    fetchAccounts({ lastSyncStatus: status, page: 1 });
    setLastSyncStatus(status);
    setPage(0);
  };

  const handleColumnToggle = (colId) => {
    let newVisible;
    if (visibleColumns.includes(colId)) {
      newVisible = visibleColumns.filter(id => id !== colId);
    } else {
      newVisible = [...visibleColumns, colId];
    }
    setVisibleColumns(newVisible);
    localStorage.setItem('simplefin_visible_columns', JSON.stringify(newVisible));
  };

  const handleColumnsReorder = (reorderedColumns) => {
    try {
      const columnOrder = reorderedColumns.map(col => col.id);
      localStorage.setItem('simplefin_accounts_column_order', JSON.stringify(columnOrder));
    } catch (e) {
      console.error('Failed to save column order', e);
    }
  };

  const applyBulkStatus = async () => {
    if (!bulkStatus) return;
    const ids = Array.from(selectedIds);
    const prev = accounts;
    const updated = accounts.map(a => ids.includes(a.id) ? { ...a, card_status: bulkStatus } : a);
    setAccounts(updated);

    try {
      await Promise.all(ids.map(id => simplefinTransactions.updateAccountStatus(id, bulkStatus)));
      try {
        const raw = localStorage.getItem('simplefin_account_statuses');
        const map = raw ? JSON.parse(raw) : {};
        ids.forEach(id => { map[id] = bulkStatus; });
        localStorage.setItem('simplefin_account_statuses', JSON.stringify(map));
      } catch (e) { console.error('Failed to persist statuses locally', e); }
      clearSelection();
    } catch (err) {
      console.error('Bulk update failed, reverting', err);
      setAccounts(prev);
      try {
        const raw = localStorage.getItem('simplefin_account_statuses');
        const map = raw ? JSON.parse(raw) : {};
        ids.forEach(id => { map[id] = bulkStatus; });
        localStorage.setItem('simplefin_account_statuses', JSON.stringify(map));
      } catch (e) { console.error(e); }
    }
  };

  const isAmex = (bankName) => {
    return bankName && (
      bankName.toLowerCase().includes('american express') || 
      bankName.toLowerCase().includes('amex')
    );
  };

  const allColumnsDef = [
    { id: 'id', label: 'ID', sortable: true, sortKey: 'id' },
    { id: 'customer_name', label: 'Name', sortable: true, sortKey: 'customer_name' },
    { 
      id: 'name', 
      label: 'Account Name', 
      sortable: true, 
      sortKey: 'name',
      render: (row) => (
        <span 
          onClick={() => {
            const acct = (row.name || '').trim();
            window.open(`/simplefin-management?accountName=${encodeURIComponent(acct)}`, '_blank');
          }}
          style={{ fontWeight: 500, color: '#90caf9', cursor: 'pointer', textDecoration: 'underline' }}
        >
            {row.name}
        </span>
      )
    },
    { id: 'bank', label: 'Bank', sortable: true, sortKey: 'bank' },
    { 
      id: 'balance', label: 'Current Balance', sortable: true, sortKey: 'balance',
      render: (row) => (
        <span style={{ color: row.balance < 0 ? '#f44336' : '#66bb6a', fontWeight: 'bold' }}>
          {formatCurrency(row.balance, row.currency)}
        </span>
      )
    },
    { 
      id: 'total_credit_line', 
      label: 'Total Credit Limit', 
      sortable: true, 
      sortKey: 'total_credit_line',
      render: (row) => {
        if (isAmex(row.bank) && (row.total_credit_line === 0 || row.total_credit_line === null)) {
            return <span style={{ fontStyle: 'italic', color: '#aaa', fontSize: '0.85rem' }}>No Preset Limit</span>;
        }
        return formatCurrency(row.total_credit_line);
      }
    },
    { 
      id: 'last_synced', label: 'Last Synced', sortable: true, sortKey: 'last_synced',
      render: (row) => row.last_synced ? row.last_synced : '-'
    },
    { 
      id: 'last_statement_opening', label: 'Last Statement Opening', sortable: true, sortKey: 'last_statement_opening',
      render: (row) => row.last_statement_opening ? row.last_statement_opening : '-'
    },
    { 
      id: 'last_statement_closing', label: 'Last Statement Closing', sortable: true, sortKey: 'last_statement_closing',
      render: (row) => row.last_statement_closing ? row.last_statement_closing : '-'
    },
    { 
      id: 'payment_autopay_date', label: 'Autopay Date', sortable: true, sortKey: 'payment_autopay_date',
      render: (row) => row.payment_autopay_date ? row.payment_autopay_date : '-'
    },
    { 
      id: 'autopay_bank_account', label: 'Autopay Bank (name / ending)', sortable: false,
      render: (row) => row.autopay_bank_account ? row.autopay_bank_account : '-'
    },
    { 
      id: 'last_sync_status', label: 'Sync Status', sortable: true, sortKey: 'last_sync_status',
      render: (row) => getShortSyncStatus(row.last_sync_status)
    },
    { 
        id: 'apr', label: 'APR', sortable: true, sortKey: 'apr',
        render: (row) => row.apr ? `${row.apr}%` : '-'
    },
    { id: 'user_name', label: 'Email', sortable: true, sortKey: 'user_name' },
    {
      id: 'card_status',
      label: 'Card Status',
      sortable: true,
      sortKey: 'card_status',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StatusCell
            row={row}
            labels={statusLabels}
            value={row.card_status}
            onChange={(val) => handleStatusChange(row, val)}
            onLabelsEdit={() => setEditingLabels(true)}
          />
          <IconButton size="small" onClick={() => openNotesFor(row)} title="Notes">
            <NoteAddIcon />
          </IconButton>
        </div>
      )
    },
  ];

  const getDisplayedColumns = () => {
    const filtered = allColumnsDef.filter(col => visibleColumns.includes(col.id));
    try {
      const savedOrder = localStorage.getItem('simplefin_accounts_column_order');
      if (savedOrder) {
        const orderArray = JSON.parse(savedOrder);
        return filtered.sort((a, b) => {
          const aIndex = orderArray.indexOf(a.id);
          const bIndex = orderArray.indexOf(b.id);
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
      }
    } catch (e) {
      console.error('Failed to apply saved column order', e);
    }
    return filtered;
  };

  const displayedColumns = getDisplayedColumns();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setPage(parseInt(params.get('page') || '1', 10) - 1);
    setRowsPerPage(parseInt(params.get('rowsPerPage') || '50', 10));
    setKeyword(params.get('search') || '');
    setCustomerSearch(params.get('customer') || '');
    setBankSearch(params.get('bank') || '');
    setMinBalance(params.get('minBal') || '');
    setMaxBalance(params.get('maxBal') || '');
    setMinCredit(params.get('minCred') || '');
    setMaxCredit(params.get('maxCred') || '');
    setOrderBy(params.get('orderBy') || 'name');
    setOrderDirection(params.get('orderDirection') || 'asc');
    setStatusFilter(params.get('status') || '');
    setLastSyncStatus(params.get('lastSyncStatus') || '');
  }, []);

  useEffect(() => {
    fetchAccounts();
    updateUrlParams();
  }, [page, rowsPerPage, orderBy, orderDirection, keyword, customerSearch, bankSearch, minBalance, maxBalance, minCredit, maxCredit, lastSyncStatus]);

  useEffect(() => {
    fetchAccounts();
  }, [statusFilter]);

  const updateUrlParams = () => {
    const params = new URLSearchParams();
    params.set('page', page + 1);
    params.set('rowsPerPage', rowsPerPage);
    params.set('orderBy', orderBy);
    params.set('orderDirection', orderDirection);
    if (keyword) params.set('search', keyword);
    if (customerSearch) params.set('customer', customerSearch);
    if (bankSearch) params.set('bank', bankSearch);
    if (statusFilter) params.set('status', statusFilter);
    if (minBalance) params.set('minBal', minBalance);
    if (maxBalance) params.set('maxBal', maxBalance);
    if (minCredit) params.set('minCred', minCredit);
    if (maxCredit) params.set('maxCred', maxCredit);
    if (lastSyncStatus) params.set('lastSyncStatus', lastSyncStatus);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const fetchAccounts = async (overrides = {}) => {
    const effectivePage = overrides.page !== undefined ? overrides.page : (page + 1);
    const effectiveLastSync = overrides.lastSyncStatus !== undefined ? overrides.lastSyncStatus : lastSyncStatus;

    const params = {
        page: effectivePage,
        pageSize: rowsPerPage,
        keyword,
        customerName: customerSearch || null,
        bankName: bankSearch || null,
        status: statusFilter || null,
        minBalance: minBalance || null,
        maxBalance: maxBalance || null,
        minCreditLine: minCredit || null,
        maxCreditLine: maxCredit || null,
        orderBy,
        orderDirection,
        lastSyncStatus: effectiveLastSync || null
    };

    try {
      const res = await simplefinTransactions.getAccountsPaginated(params);
      if (res && res.success) {
          const saved = {};
          try {
            const raw = localStorage.getItem('simplefin_account_statuses');
            if (raw) Object.assign(saved, JSON.parse(raw));
          } catch (e) { }

          const merged = res.result.accounts.map(a => ({ ...a, card_status: (a.card_status && a.card_status !== '') ? a.card_status : (saved[a.id] || '') }));
          setAccounts(merged);
          setTotalCount(res.result.totalCount);
      } else {
          setAccounts([]);
          setTotalCount(0);
      }
    } catch (err) {
      console.error("Failed to fetch accounts", err);
      setAccounts([]);
    }
  };

  const handleStatusChange = (row, val) => {
    const prev = accounts;
    const next = accounts.map(a => a.id === row.id ? { ...a, card_status: val } : a);
    setAccounts(next);

    simplefinTransactions.updateAccountStatus(row.id, val).then(() => {
      try {
        const raw = localStorage.getItem('simplefin_account_statuses');
        const map = raw ? JSON.parse(raw) : {};
        if (val === '' || val === null || typeof val === 'undefined') {
          delete map[row.id];
        } else {
          map[row.id] = val;
        }
        localStorage.setItem('simplefin_account_statuses', JSON.stringify(map));
      } catch (e) { console.error('Failed saving status locally', e); }
    }).catch((err) => {
      console.error('Failed to update status on server, reverting', err);
      setAccounts(prev);
      try {
        const raw = localStorage.getItem('simplefin_account_statuses');
        const map = raw ? JSON.parse(raw) : {};
        map[row.id] = val;
        localStorage.setItem('simplefin_account_statuses', JSON.stringify(map));
      } catch (e) { console.error(e); }
    });
  };

  const openNotesFor = (row) => {
    setSelectedAccount(row);
    setNotesOpen(true);
  };

  const closeNotes = () => {
    setSelectedAccount(null);
    setNotesOpen(false);
  };

  const handleSortChange = (key) => {
    const isAsc = orderBy === key && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(key);
  };

  const handleClear = () => {
    setKeyword('');
    setCustomerSearch('');
    setBankSearch('');
    setMinBalance('');
    setMaxBalance('');
    setMinCredit('');
    setMaxCredit('');
    setPage(0);
  };

  const handleBack = () => {
    window.location.href = '/simplefin-management';
  };

  const inputStyle = { bgcolor: '#2c2c2c', borderRadius: 1 };

  return (
    <div style={{ marginTop: "20px", margin: "auto" }}>
      <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
        
        <AppBar position="static" sx={{ bgcolor: '#1e1e1e' }}>
            <Toolbar>
                <Tooltip title="Back to Transaction Manager">
                    <IconButton 
                        edge="start" color="inherit" aria-label="back" onClick={handleBack} sx={{ mr: 2 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                </Tooltip>

                <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                    SIMPLEFIN ACCOUNTS MANAGER
                </Typography>
            </Toolbar>
        </AppBar>
        <Box component="main" sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: '#121212', color: 'white' }}>
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#1e1e1e', color: 'white' }}>
              <Typography variant="caption" sx={{ mb: 1, color: 'gray', fontWeight: 600, display: 'block' }}>FILTERS & SEARCH</Typography>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Search"
                    variant="outlined" size="small" fullWidth
                    value={keyword} onChange={(e) => { setKeyword(e.target.value); setPage(0); }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{color: 'gray', fontSize: '1.1rem'}}/></InputAdornment> }}
                    sx={inputStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Customer"
                    variant="outlined" size="small" fullWidth
                    value={customerSearch} onChange={(e) => { setCustomerSearch(e.target.value); setPage(0); }}
                    sx={inputStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Bank"
                    variant="outlined" size="small" fullWidth
                    value={bankSearch} onChange={(e) => { setBankSearch(e.target.value); setPage(0); }}
                    sx={inputStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Select
                    value={statusFilter}
                    displayEmpty
                    onChange={(e) => {setStatusFilter(e.target.value); setPage(0);}}
                    size="small"
                    fullWidth
                    sx={{ ...inputStyle, color: 'white', height: 40 }}
                  >
                    <MenuItem value="">All Status</MenuItem>
                    {statusLabels.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField placeholder="Min" type="number" size="small" fullWidth value={minBalance} onChange={e => {setMinBalance(e.target.value); setPage(0);}} sx={inputStyle} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField placeholder="Max" type="number" size="small" fullWidth value={maxBalance} onChange={e => {setMaxBalance(e.target.value); setPage(0);}} sx={inputStyle} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField placeholder="Min" type="number" size="small" fullWidth value={minCredit} onChange={e => {setMinCredit(e.target.value); setPage(0);}} sx={inputStyle} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField placeholder="Max" type="number" size="small" fullWidth value={maxCredit} onChange={e => {setMaxCredit(e.target.value); setPage(0);}} sx={inputStyle} />
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <Button variant="contained" color="primary" onClick={() => setPage(0)} fullWidth sx={{ height: 40, fontWeight: 'bold' }}>
                    Search
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <Button variant="outlined" color="secondary" onClick={handleClear} fullWidth sx={{ height: 40, fontWeight: 'bold' }}>
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Paper>
            {/* COLUMN VISIBILITY + BULK ACTIONS */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    {selectedIds.size > 0 && (
                        <Paper sx={{ p: 1, px: 2, display: 'flex', gap: 2, alignItems: 'center', bgcolor: '#252525' }}>
                            <Typography variant="body2">{selectedIds.size} selected</Typography>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select 
                                    value={bulkStatus} 
                                    onChange={(e) => setBulkStatus(e.target.value)}
                                    displayEmpty
                                    sx={{ height: 32, color: 'white', borderColor: '#555' }}
                                >
                                    <MenuItem value="" disabled>Status</MenuItem>
                                    {statusLabels.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <Button variant="contained" size="small" onClick={applyBulkStatus} disabled={!bulkStatus}>Apply</Button>
                            <Button variant="outlined" size="small" onClick={clearSelection}>Clear</Button>
                        </Paper>
                    )}
                </Box>

                <Box>
                    <Button 
                        startIcon={<ViewColumnIcon />} 
                        variant="outlined" 
                        onClick={(e) => setColMenuAnchor(e.currentTarget)}
                        sx={{ color: '#aaa', borderColor: '#444' }}
                    >
                        Columns
                    </Button>
                    <Menu
                        anchorEl={colMenuAnchor}
                        open={Boolean(colMenuAnchor)}
                        onClose={() => setColMenuAnchor(null)}
                        PaperProps={{ sx: { bgcolor: '#1e1e1e', color: 'white', maxHeight: 300 } }}
                    >
                        {allColumnsDef.map((col) => (
                            <MenuItem key={col.id} onClick={() => handleColumnToggle(col.id)}>
                                <FormControlLabel
                                    control={
                                        <Checkbox 
                                            checked={visibleColumns.includes(col.id)} 
                                            size="small"
                                            sx={{ color: 'gray', '&.Mui-checked': { color: '#90caf9' } }}
                                        />
                                    }
                                    label={col.label}
                                    sx={{ pointerEvents: 'none' }}
                                />
                            </MenuItem>
                        ))}
                        <MenuItem divider />
                        <MenuItem onClick={() => {
                            setVisibleColumns(allColumnIds);
                            localStorage.setItem('simplefin_visible_columns', JSON.stringify(allColumnIds));
                            localStorage.removeItem('simplefin_accounts_column_order');
                        }}>
                            <Typography variant="caption" color="primary">Reset to All</Typography>
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>

            <DraggableTable
                data={accounts}
                columns={displayedColumns}
                totalCount={totalCount}
                page={page}
                rowsPerPage={rowsPerPage}
                orderBy={orderBy}
                orderDirection={orderDirection}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                onSortChange={handleSortChange}
                onColumnsReorder={handleColumnsReorder}
                selection={{
                  selectedIds,
                  onToggle: toggleSelect,
                  onToggleAll: (checked) => {
                    if (checked) setSelectedIds(new Set(accounts.map(a => a.id)));
                    else setSelectedIds(new Set());
                  },
                  allSelected: selectedIds.size > 0 && selectedIds.size === accounts.length
                }}
            />
            
            <Dialog open={editingLabels} onClose={() => setEditingLabels(false)} fullWidth maxWidth="sm">
              <DialogTitle>Edit Card Status Labels</DialogTitle>
              <DialogContent>
                <TextField
                  label="Comma-separated labels"
                  fullWidth
                  multiline
                  minRows={2}
                  value={labelsText || statusLabels.join(', ')}
                  onChange={(e) => setLabelsText(e.target.value)}
                  sx={{ bgcolor: '#1e1e1e', mt: 1 }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => { setEditingLabels(false); setLabelsText(''); }}>Cancel</Button>
                <Button onClick={async () => {
                  const arr = (labelsText || statusLabels.join(', ')).split(',').map(s => s.trim()).filter(Boolean);
                  try {
                    await simplefinTransactions.updateStatusLabels(arr);
                    setStatusLabels(arr);
                    setEditingLabels(false);
                    setLabelsText('');
                  } catch (e) {
                    console.error('Error saving status labels:', e);
                    alert('Failed to save status labels');
                  }
                }} variant="contained">Save</Button>
              </DialogActions>
            </Dialog>

            <NotesSidebar open={notesOpen} onClose={closeNotes} account={selectedAccount} />
        </Box>
      </Box>
    </div>
  );
}
